/* eslint-disable react/destructuring-assignment */
/* eslint-disable @typescript-eslint/no-empty-function */

import * as _ from 'lodash';
import * as React from 'react';
import { BeatLoader } from 'react-spinners';
import { Dispatch } from 'redux';
import { newUUID, UserViewModel } from 'stageclip-common';

import { createClearRequestErrorsAction } from '../actions/errorActions';
import { LOGGER } from '../config';
import { ErrorDetails } from '../models/errorDetails';
import { ClippingPlatformState } from '../store/clippingPlatformState';
import { getItem } from '../store/selectors';

import styles from './componentBase.module.scss';

export interface BaseStateProps {
  currentUser: UserViewModel;
  possibleLoadingErrors: ErrorDetails[];
  loggedIn?: boolean;
}

export interface BaseDispatchProps {
  clearErrors: (c: React.Component) => void;
}

export abstract class ComponentBase<
  OwnProps = {},
  StateProps extends BaseStateProps = BaseStateProps,
  DispatchProps extends BaseDispatchProps = BaseDispatchProps,
  InnerState = {}
> extends React.Component<OwnProps & StateProps & DispatchProps, InnerState> {
  // eslint-disable-next-line camelcase
  public async UNSAFE_componentWillMount() {
    await this.dataUpdate(this.props);
  }

  public shouldComponentUpdate(nextProps: any, nextState: any) {
    const component = this.constructor.name;
    const shouldUpdate = this.shouldUpdateCondition(nextProps, nextState);

    if (shouldUpdate) {
      LOGGER.debug(`${component} is updating`);
    }

    return shouldUpdate;
  }

  // eslint-disable-next-line camelcase
  public async UNSAFE_componentWillUpdate(nextProps: any) {
    await this.props.clearErrors(this);
    await this.dataUpdate(nextProps);
  }

  public async componentWillUnmount() {
    await this.props.clearErrors(this);
  }

  private getErrorsForThisComponent(): ErrorDetails[] {
    return this.props.possibleLoadingErrors.filter((e: ErrorDetails) => e.component === this);
  }

  // noinspection JSMethodCanBeStatic
  private propsConsideredForUpdate(props: any) {
    return {
      ...props,
      currentErrors: props.possibleLoadingErrors
        .filter((e: ErrorDetails) => e.component === this)
        .reduce((result: any, e: ErrorDetails) => ({ ...result, [e.message]: true }), {}),
      possibleLoadingErrors: undefined,
    };
  }

  private stateConsideredForUpdate(state: InnerState) {
    return state;
  }

  private shouldUpdateCondition(nextProps: any, nextState: any) {
    return (
      !_.isEqual(this.propsConsideredForUpdate(this.props), this.propsConsideredForUpdate(nextProps)) ||
      !_.isEqual(this.stateConsideredForUpdate(this.state), this.stateConsideredForUpdate(nextState))
    );
  }

  protected displayError(): JSX.Element {
    return (
      <div className="error">
        {this.getErrorsForThisComponent().map(e => (
          <p key={newUUID()}>{e.message}</p>
        ))}
      </div>
    );
  }

  protected async dataUpdate(props: any = this.props) {
    if (!this.allDataLoaded(props)) {
      await this.loadAllData(props);
    }
  }

  protected loggedOutRender(): JSX.Element | string {
    return 'Please log in before using the app';
  }

  // eslint-disable-next-line no-unused-vars,no-empty-function
  protected async loadData(props: any = this.props) {}

  // eslint-disable-next-line no-unused-vars
  protected dataLoaded(props?: any): boolean {
    return true;
  }

  protected abstract loggedInRender(): JSX.Element | string;

  private allDataLoaded(props: any) {
    return this.dataLoaded(props);
  }

  private async loadAllData(props: any) {
    await this.loadData(props);
  }

  public render(): JSX.Element | string {
    if (this.getErrorsForThisComponent().length > 0) {
      return this.displayError();
    }

    if (!this.allDataLoaded(this.props)) {
      return (
        <div className={styles.loadingSpinner}>
          <BeatLoader />
        </div>
      );
    }

    try {
      if (this.props.loggedIn) {
        return this.loggedInRender();
      }

      return this.loggedOutRender();
    } catch (e) {
      LOGGER.error(e);

      return <div className="error">{e.message}</div>;
    }
  }
}

export function baseMapStateToProps(state: ClippingPlatformState): BaseStateProps {
  const { session } = state.appStatus;

  return {
    currentUser: getItem(state.entities.users, session.user),
    loggedIn: !_.isNil(session.sessionToken),
    possibleLoadingErrors: state.errors.loadingErrors,
  };
}

export function baseMapDispatchToProps(dispatch: Dispatch): BaseDispatchProps {
  return {
    clearErrors: c => dispatch(createClearRequestErrorsAction(c)),
  };
}
